// defense 열람, **roster 아코디언. 내가 언제 보내는가**
//
// 공격 열람이 「내 rally 가 언제 열리는가」를 말한다면 여기는 「내 squad 를 언제 보내는가」를
// 말한다. 한 사람이 적 rally 여럿에 대해 각각 보낼 시점을 갖고, 그 줄들은 착탄 순으로 선다.
// 사람의 순서는 명부 순서 그대로이고 시간이 흘러도 바뀌지 않는다(2026-08-25 사용자).
//
// ── 소리 장치를 만들지 않는다 ───────────────────────────────────────────────
//
// 2026-08-05 결정이고 이 화면의 계약이다. 여기서 소리를 내면 **공격 열람이 이미 통과한
// 「소리가 두 개 겹치는 순간이 없다」가 깨진다** — 그 항목은 통과·불통이라 부분 점수가 없고,
// 축 C 25점 중 5점이 거기 걸려 있어 한쪽만 무너져도 축이 하한 아래로 간다. 행동의 기준은
// 화면의 트리거다.
//
// ── 불가를 숨기지 않는 것이 계약이다 ────────────────────────────────────────
//
// 전 rally 에 대해 불가인 사람이 화면에서 사라지면 불통이다. **그 사람이 못 간다는 사실이
// CT 가 배정을 바꿀 근거**이기 때문이다. 목록을 「깔끔하게」 정리하려는 이후 수정이 조용히
// 되돌릴 수 있는 종류라, 브라우저 검사가 그 자리에 못을 박는다.
//
// ── 흐려질 뿐 자리를 지킨다 ─────────────────────────────────────────────────
//
// 보낼 것이 없는 사람은 섹션째 흐려지고, 지나간 대응 줄도 흐려진다. 어느 쪽도 아래로
// 내려가지 않는다. 내리는 것이 곧 시간에 따른 재정렬이고, 2026-08-25 사용자가 그것을
// 금지했다. 순서는 `src/web/defense/derive.ts` 가 정하고 이 화면은 그대로 그린다.
//
// ── 판정을 여기서 내리지 않는다 ─────────────────────────────────────────────
//
// 트리거도 보낼 시점도 불가도 `src/web/defense/derive.ts` 가 코어에서 받아 온 것을 그대로
// 그린다. 이 화면이 `− δ` 한 줄을 자기가 쓰는 순간 부호가 두 곳에 살고, 그 어긋남은
// 그럴듯한 숫자로 뜬다.

import { useMemo } from 'react'

import { duration, toMillis } from '../../../core/time/brand.ts'
import { formatDuration } from '../../../core/timing/format.ts'
import type { TriggerPhase } from '../../../core/timing/defense.ts'
import { DEFAULT_LANG, type Lang, defense as defenseStrings } from '../../../i18n/index.ts'
import { type RosterSection, rosterSections, type SquadCell } from '../../defense/derive.ts'
import type { DefenseStore } from '../../defense/use-defense.ts'
import { Figures } from '../../ui/figures.tsx'
import { IconTile } from '../../ui/icons.tsx'
import { LabelHint } from '../../ui/label-hint.tsx'
import { formatRemaining, formatSinceOpen } from '../view-attack/live-clock.ts'

export interface ViewDefenseScreenProps {
  store: DefenseStore
  /** 껍데기가 읽은 지금. **화면 전체가 같은 값을 쓴다** — 각자 읽으면 같은 화면의 두 숫자가
   *  서로 다른 순간을 가리키고, 그 어긋남은 밀리초라 눈에 안 보인다. */
  nowMs: number
  lang?: Lang
  /**
   * attack 탭에서 고른 소리가 지금 걸려 있는가. **문장 하나를 가르는 값이다.**
   *
   * 「수비에는 소리 신호가 없습니다」는 수비 자체를 두고 하는 옳은 말인데, attack 신호가
   * 살아 있는 동안에는 **거짓으로 읽힌다** — 소리는 울리는데 화면이 없다고 말한다. 그때는
   * 두 사실을 함께 말하는 문장으로 바꾼다. 2026-08-16 대리 관측이 이 자리를 잡았다.
   */
  signalLive?: boolean
}

export function ViewDefenseScreen(props: ViewDefenseScreenProps): React.ReactElement {
  const t = defenseStrings(props.lang ?? DEFAULT_LANG)
  const { store, nowMs } = props

  const sections = useMemo(
    () => rosterSections(store.enemyRallies, store.rosters, nowMs),
    [store.enemyRallies, store.rosters, nowMs],
  )

  return (
    // 폭은 attack 열람과 같은 `max-w-2xl` 로 모은다(2026-08-25 사용자). 딥틸 배경은 같은 날
    // 없앴다. 「지금 보고 있는 것이 무엇인가」는 배경이 아니라 하단 탭과 제목이 말한다.
    <main
      data-testid="view-defense"
      data-loaded={String(store.loaded)}
      className="view-main col-narrow min-h-[60vh] pt-group pb-block font-sans"
    >
      {/* 수비 열람의 제목은 화면에 남긴다(2026-10-13 사용자, c2 비교판을 보고 고름). attack 열람만 제목 줄을 화면 읽개 전용으로 돌렸다.
          수비까지 걷으면 화면 첫머리가 설명 문단과 R/M 범례 상자로 시작해 설명서처럼 읽힌다(비평 둘의 지적). */}
      {/* 제목은 라벨(수비)과 뜻풀이(내가 보낼 시점) 두 요소다. 뜻풀이는 한 단계 작고 가는 글자로 같은 줄 곁에 선다(2026-10-14 사용자 지시). */}
      <h1 className="page-title text-lg font-semibold">
        <IconTile name="mark" tone="accent" size="sm" />
        <LabelHint label={t.viewTitle} hint={t.viewTitleHint} hintClassName="text-sm font-normal text-ink-dim" />
      </h1>
      <p className="mt-line text-xs text-ink-dim">{t.viewWhy}</p>

      {/* R 과 M 의 뜻풀이(2026-08-25 사용자). 칩의 글자가 카탈로그의 것이라 이 문장도
          카탈로그에서 읽는다. 색이 뜻을 혼자 지지 않는다는 규칙의 셋째 채널이다. 두 칩 색은
          적록색약 시뮬레이션에서 명암비 1.02 라 색만으로는 안 갈린다(2026-08-17 대리 채점). */}
      <section
        data-testid="defense-guide"
        aria-label={t.guideTitle}
        className="note mt-group text-xs text-ink-dim"
      >
        <p>
          <span className={`font-semibold ${PHASE_TEXT.rallying}`}>{t.triggerRallying}</span>{' '}
          {t.guideRallying}
        </p>
        <p className="mt-line">
          <span className={`font-semibold ${PHASE_TEXT.marching}`}>{t.triggerMarching}</span>{' '}
          {t.guideMarching}
        </p>
        <p className="mt-line">{t.guideOrder}</p>
      </section>

      {!store.loaded ? (
        <p className="mt-block text-sm text-ink-mute">{t.viewTitle}</p>
      ) : store.enemyRallies.length === 0 ? (
        <p data-testid="view-defense-empty" className="mt-block text-sm text-ink-dim">
          {t.viewEmpty}
        </p>
      ) : (
        <div className="mt-block space-y-group">
          {sections.map((section) => (
            <Section key={section.roster.id} section={section} nowMs={nowMs} t={t} />
          ))}
        </div>
      )}

      {/* **소리가 없다는 사실을 화면이 말한다.** 공격 열람에서 소리를 들은 사람이 이 탭에
          와서 아무것도 안 들리면 자기 기기를 의심한다 — 없는 것을 없다고 말하지 않으면
          그 침묵이 고장으로 읽힌다.

          **다만 attack 신호가 살아 있는 동안에는 그 문장이 거짓이 된다.** 소리는 울리는데
          화면이 없다고 말하는 상태이고, 이 저장소가 여러 번 값을 매긴 「화면이 자기 상태를
          잘못 말한다」와 같은 종류다. 그때는 두 사실을 함께 말한다. */}
      <p data-testid="view-defense-no-sound" className="mt-section text-xs text-ink-mute">
        {props.signalLive === true ? t.viewNoSoundWithAttack : t.viewNoSound}
      </p>
    </main>
  )
}

/**
 * roster 한 사람. **기본으로 펼쳐져 있다** — 정본이 「기본 전부 펼침」을 계약했다.
 *
 * `details` 를 쓰는 이유는 접고 펴는 일을 브라우저가 이미 접근성까지 갖춰 하고 있기
 * 때문이다. 직접 만들면 키보드와 낭독기 처리를 한 벌 더 쓰게 되고, 그 한 벌은 반드시
 * 원본보다 얕다.
 */
function Section(props: {
  section: RosterSection
  nowMs: number
  t: ReturnType<typeof defenseStrings>
}): React.ReactElement {
  const { section, t } = props
  const dim = section.dimReason !== null
  const first = section.cells.find((cell) => cell.plan.status === 'ready')

  // 사유는 라벨(굵게)과 풀이 문장 두 요소다(2026-10-14 사용자 지시, 대시로 잇지 않는다). 「지금 보낼 수 있는 대응이 없습니다」는
  // 그 자체가 한 문장이라 풀이가 없다.
  const reason: { label: string; why: string } =
    section.dimReason === 'marching-missing'
      ? { label: t.dimMarchingMissing, why: t.dimMarchingMissingWhy }
      : section.dimReason === 'all-impossible'
        ? {
            label: t.dimAllImpossible,
            why: t.dimAllImpossibleWhy.replaceAll(
              '{marching}',
              // 값이 없을 때의 대시 하나는 시간 미상 자리 표기라 화면에 그대로 둔다(2026-10-14 사용자: 시간 미상 표기는 유지).
              // 소스에는 그 글자를 직접 쓰지 않는 규칙(2026-08-20 사용자)이 있어 이스케이프로 적는다.
              section.roster.marchingMs === null ? '\u2014' : safeDuration(section.roster.marchingMs),
            ),
          }
        : { label: t.dimNothing, why: '' }

  return (
    <details
      open
      data-testid="defense-section"
      data-roster={section.roster.nickname}
      data-dim={String(dim)}
      className={`card ${dim ? 'text-ink-mute' : ''}`}
    >
      <summary className="panel-head cat-cyan flex-wrap justify-between gap-x-2 gap-y-line">
        <span data-testid="section-name" className="flex min-w-0 items-center gap-2.5 truncate text-base font-semibold">
          {/* c2: 방패(수비 범주) 대신 사람 한 명. 이 머리는 아군 한 사람의 묶음이다(SaaS 비평). */}
          <IconTile name="user" tone="cyan" size="sm" />
          {section.roster.nickname}
        </span>
        {/* **머리의 요약도 아래 목록과 같은 규칙을 쓴다.** 색이 뜻을 지기 시작한 뒤에는
            같은 `R` 이 여기서는 무채색이고 바로 아래 줄에서는 초록인 것이 「이 R 은 왜
            초록이 아닌가」를 만든다. 한 화면 안에서 같은 값이 두 규칙으로 살면 사람이
            그 둘의 차이에 뜻이 있다고 읽는다. (2026-08-17 대리 관측이 이 자리를 잡았다.)
            상자를 만들지는 않는다 — 이 자리는 요약이라 아래 목록과 무게가 같아지면 안 된다. */}
        <span data-testid="section-next" className="shrink-0 font-mono text-sm font-medium tabular-nums">
          {first !== undefined && first.plan.status === 'ready' ? (
            <>
              {/* **속성으로도 단계를 말한다.** 글자가 카탈로그의 것이 된 뒤로는 글자만 보고
                  단계를 되짚을 수 없다 — 언어가 바뀌면 그 글자가 바뀐다. 아래 목록의 칩이
                  같은 이유로 이미 `data-phase` 를 들고 있다. */}
              <span
                data-testid="section-next-phase"
                data-phase={first.plan.trigger.phase}
                className={PHASE_TEXT[first.plan.trigger.phase]}
              >
                {triggerLabel(first.plan.trigger.phase, t)}
              </span>{' '}
              {formatDuration(first.plan.trigger.remaining)}
            </>
          ) : (
            t.sectionNone
          )}
        </span>
      </summary>

      {/* **사유를 글자로 적는다.** 흐리게만 두면 왜 흐린지가 색에만 실리고, 색 단독에
          의존하지 않는다는 것이 이 화면의 계약이다. */}
      {dim && (
        <p data-testid="section-dim-reason" className="mt-line text-xs">
          <span className="font-semibold">{reason.label}</span>
          {reason.why !== '' && <span className="ml-1.5">{reason.why}</span>}
          <span className="ml-1.5 text-ink-mute">{t.dimWhy}</span>
        </p>
      )}

      {section.cells.length > 0 && (
        <ul className="mt-group divide-y divide-line-hair">
          {section.cells.map((cell) => (
            <Cell key={cell.enemyRallyId} cell={cell} nowMs={props.nowMs} t={t} />
          ))}
        </ul>
      )}
    </details>
  )
}

/**
 * 트리거 칩의 테두리 색 — **단계마다 다르다** (2026-08-17 사용자 지시).
 *
 * 2026-08-16까지 R 과 M 이 같은 초록을 썼다. 정본이 못박은 것은 모양(라운드 코너)과
 * 글자(`R`·`M`)뿐이라 색은 비어 있었는데, 비워 두면 같은 값을 쓰는 것이 가장 쉬운 답이 된다.
 *
 * **왜 M 이 따뜻한 쪽인가.** 초록에서 주황으로 가는 것이 **「아직 안 떴다 → 이미 날아온다」의
 * 급함 단계**로 읽힌다. 두 단계의 차이가 정확히 그것이다 — R 은 보낼 순간에 적이 아직
 * rallying 중이라는 뜻이고 M 은 그때 이미 행군 중이라는 뜻이다.
 *
 * **금색(`#E8C35C`)을 쓰지 않은 이유가 있고 그것이 이 표의 진짜 근거다.** 처음 고른 값이
 * 그것이었는데, 그 색은 **이미 main rally 의 색이고 main 의 표식 문자가 `M` 이다.** 그대로
 * 두면 같은 연맹원이 attack 탭에서 금색 `M`(main rally)을, defense 탭에서 금색 `M`(marching
 * 단계)을 본다. 같은 글자에 같은 색인데 뜻이 다르고, 두 탭은 전투 중에 오가는 자리다.
 * 색을 고를 때 팔레트에 그 값이 있는지만 보면 이 충돌이 안 보인다 — **이미 무슨 뜻으로
 * 쓰이는지**를 함께 봐야 한다.
 *
 * 주황은 이 저장소에서 「지금 누를 것」에 쓰이지만 **어느 글자의 뜻도 아니다.** 그리고 이
 * 칩은 버튼처럼 보이지 않는다(채움 없이 테두리와 글자뿐이다).
 *
 * **표로 빼는 이유.** 삼항으로 쓰면 다음 사람이 한쪽만 고친다. rally 타입 색이 이미 같은
 * 문법을 쓰고 있어(`KIND_BORDER`) 저장소가 한 문법으로 남는다.
 *
 * **색이 뜻을 혼자 지지 않는다.** 칩 안에 글자가 함께 있어 색을 못 가르는 사람에게도 단계가
 * 남는다. 이 화면이 흐린 섹션의 사유를 글자로 적는 것과 같은 규칙이다.
 */
const PHASE_BORDER: Record<TriggerPhase, string> = {
  // R 은 421d341 의 초록 `#9ECE6A` 를 그 판과 같은 방식(이 파일의 임의값 클래스)으로 쓴다(2026-10-13 사용자, D4 구현 뒤 고른 셋).
  // D4 미리보기 a 부터 묻지 않고 시안으로 바뀌어 있었고, 시안은 보류한 DEFENSE 범주색과 겹쳤다. 칩 재료(옅은 면 12%, 같은 색 35% 선)는
  // `chip-cyan` 과 같게 맞추고 색만 초록이다. 칩에는 테두리가 없어 계산된 테두리 색은 글자색을 따른다(e2e 가 그 값을 읽는다).
  rallying: 'bg-[#9ECE6A]/12 text-[#9ECE6A] shadow-[inset_0_0_0_1px_rgb(158_206_106/0.35)]',
  marching: 'chip-accent',
}

/**
 * 칩 안의 글자. **코어가 아니라 여기가 고른다** (2026-08-17).
 *
 * 코어가 `'R'`·`'M'` 을 값으로 내보내던 동안에는 이 표가 필요 없었다 — 화면이 코어의 값을
 * 그대로 찍으면 됐기 때문이다. 그것이 편했던 만큼 정확히 그만큼 잘못이었다: 사람이 읽는
 * 글자를 계산 코어가 정하고 있었으므로 **언어를 바꿔도 안 따라오고, 바꿀 자리조차 없었다.**
 *
 * 지금 두 언어의 값이 같다는 것은 이 배선이 쓸모없다는 뜻이 아니다. 정본이 다섯 언어를
 * 목표로 적었고, `R`·`M` 은 `rallying`·`marching` 의 머리글자라 라틴 문자를 안 쓰는 언어에서는
 * 다른 글자가 된다. **그때 고칠 자리가 카탈로그 한 줄이어야 한다.**
 */
function triggerLabel(phase: TriggerPhase, t: ReturnType<typeof defenseStrings>): string {
  return phase === 'rallying' ? t.triggerRallying : t.triggerMarching
}

/**
 * 같은 규칙의 글자색 판. **섹션 머리의 요약이 이것을 쓴다.**
 *
 * 두 표를 나란히 두는 이유는 두 자리의 모양이 다르기 때문이다 — 목록의 칩은 색 테두리를
 * 두른 상자이고 머리의 요약은 맨 글자다. 요약까지 상자로 만들면 목록과 무게가 같아져
 * 「다음 하나」라는 요약의 성질이 사라진다. **값이 갈리는 것은 색뿐이고 그 값은 위 표와
 * 같은 두 색이다** — 다르게 적으면 한쪽만 고치는 날이 온다.
 */
const PHASE_TEXT: Record<TriggerPhase, string> = {
  rallying: 'text-[#9ECE6A]',
  marching: 'text-accent',
}

/** 한 사람 × 적 rally 하나의 대응. **코어가 낸 판정을 그대로 그린다.** */
function Cell(props: {
  cell: SquadCell
  nowMs: number
  t: ReturnType<typeof defenseStrings>
}): React.ReactElement {
  const { cell, t } = props
  const plan = cell.plan

  return (
    <li
      data-testid="defense-cell"
      data-enemy-rally={cell.enemyRallyId}
      data-status={plan.status}
      // c2: 줄 위아래 여백을 12 에서 6px 로. 계기 줄이 상자를 벗어 키가 줄었다(c 119px, 목표 80 이하).
      className={`min-w-0 py-1.5 text-base ${plan.status === 'ready' ? '' : 'text-ink-dim'}`}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-line">
        <span data-testid="cell-enemy" className="min-w-0 truncate text-sm font-medium">
          {cell.enemyName ?? t.rowEnemyGone}
        </span>
        {plan.status === 'ready' && (
          // **R·M 라운드 코너 칩.** 정본이 원문으로 못박은 모양이고, 글자가 함께 있어야
          // 색만으로 뜻이 갈리지 않는다. 색은 단계마다 다르다 — 위의 표에 왜가 있다.
          <span
            data-testid="trigger-chip"
            data-phase={plan.trigger.phase}
            className={`chip shrink-0 ${PHASE_BORDER[plan.trigger.phase]}`}
          >
            {triggerLabel(plan.trigger.phase, t)}
          </span>
        )}
        {/* 보낼 수 없는 줄은 칩 자리에 상태 라벨(굵게)과 풀이(작고 흐리게)를 둔다. 전에는 「불가 대시 보낼 시점이 지났습니다」를 계기 칸
            라벨 하나에 이어 적고 값 칸에 빈 표지를 두었다(2026-10-14 사용자 지시로 라벨과 풀이를 나눔). 상자는 두지 않는다. */}
        {plan.status === 'impossible' && (
          <>
            <span data-testid="cell-impossible" className="shrink-0 text-xs font-semibold">
              {t.cellImpossible}
            </span>
            <span className="text-xs text-ink-mute">{t.cellImpossibleWhy}</span>
          </>
        )}
        {plan.status !== 'ready' && plan.status !== 'impossible' && (
          <>
            <span data-testid="cell-unknown" className="shrink-0 text-xs font-semibold">
              {t.cellUnknown}
            </span>
            <span className="text-xs text-ink-mute">{t.cellUnknownWhy}</span>
          </>
        )}
      </div>

      {plan.status === 'ready' ? (
        <Figures
          at="md"
          wide="inline"
          className="mt-line"
          figures={[
            {
              label: t.cellTrigger,
              value: formatDuration(plan.trigger.remaining),
              testId: 'cell-trigger',
            },
            {
              label: t.cellCountdown,
              value: formatRemaining(toMillis(plan.sendAt) - props.nowMs),
              testId: 'cell-countdown',
            },
          ]}
        />
      ) : plan.status === 'impossible' ? (
        <Figures
          at="md"
          wide="inline"
          className="mt-line"
          figures={[
            {
              label: t.cellLateBy,
              value: formatSinceOpen(toMillis(plan.lateBy)),
              testId: 'cell-late-by',
            },
          ]}
        />
      ) : null}
    </li>
  )
}

/**
 * 명부의 원시 밀리초를 사람이 읽는 글자로. **격자 밖 값에 화면 전체가 죽지 않게 한다.**
 *
 * 표시 형식기는 0.1초 격자를 벗어난 값에 **예외를 던진다** — 조용히 반올림하면 사람이 적은
 * 값과 계산에 쓰인 값이 갈리는데 그 차이를 아무도 못 보기 때문이고, 그 방어는 옳다. 서버가
 * 저장 경계에서 격자 밖을 막고 있지만, 그래도 들어온 값 하나가 렌더에서 던지면 React 가
 * 트리를 통째로 걷어내 **화면이 표식 하나 없이 빈다** — 열람 화면이 2026-08-14 에 실제로
 * 그렇게 죽었다. 명부 화면이 같은 자리에 둔 안전망을 그대로 빌린다: 그 값만 원시 밀리초로
 * 드러내고 목록은 산다.
 */
function safeDuration(millis: number): string {
  try {
    return formatDuration(duration(millis))
  } catch {
    return `${String(millis)} ms`
  }
}
